/**
 * Job Note Image API Service
 *
 * Handles image attachments on job notes:
 *   - Upload image to a specific note (multipart form)
 *   - Get pre-signed URL for viewing
 *   - Delete image
 *
 * Images are stored in S3 under:
 *   job/{jobUuid}/notes/{noteUuid}/{imageUuid}.{ext}
 *
 * The backend serves them through /api/job-note-images/{imageUuid}
 * which redirects to a 15-minute pre-signed S3 URL.
 */

import axiosInstance from './axiosInstance';

/**
 * Upload an image attachment to a job note.
 *
 * @param {string} jobUuid    - Parent job UUID
 * @param {string} noteUuid   - Parent note UUID
 * @param {File}   file       - The image File object from input/camera
 * @returns {Promise<{ uuid, originalFilename, contentType, fileSize, createdAt }>}
 */
export const uploadNoteImageApi = async (jobUuid, noteUuid, file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axiosInstance.post(
        `/job-note-images/jobs/${jobUuid}/notes/${noteUuid}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
};

/**
 * Get a pre-signed URL for displaying a note image.
 * Returns { imageUuid, url } where url expires in ~15 min.
 *
 * @param {string} imageUuid
 * @returns {Promise<{ imageUuid: string, url: string }>}
 */
export const getNoteImageUrlApi = async (imageUuid) => {
    const response = await axiosInstance.get(`/job-note-images/${imageUuid}/url`);
    return response.data;
};

/**
 * Delete a note image.
 *
 * @param {string} imageUuid
 * @returns {Promise<void>}
 */
export const deleteNoteImageApi = async (imageUuid) => {
    await axiosInstance.delete(`/job-note-images/${imageUuid}`);
};

/**
 * Build the redirect URL for an image (for use as img src).
 * The backend will 302 to the pre-signed S3 URL.
 * Requires the axios base URL to be prepended.
 */
export const getNoteImageSrc = (imageUuid) =>
    `/api/job-note-images/${imageUuid}`;

const jobNoteImageApi = {
    uploadNoteImageApi,
    getNoteImageUrlApi,
    deleteNoteImageApi,
    getNoteImageSrc,
};

export default jobNoteImageApi;